import React from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';

type Props = {
  page: number;
  pageCount: number;
  onPageChange: (p: number) => void;
  pageSize: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
};

const Pagination: React.FC<Props> = ({
  page,
  pageCount,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  className = ''
}) => {
  const renderPages = () => {
    const pages: Array<number | string> = [];
    if (pageCount <= 5) {
      for (let i = 1; i <= pageCount; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, '...', pageCount);
      } else if (page >= pageCount - 2) {
        pages.push(1, '...', pageCount - 2, pageCount - 1, pageCount);
      } else {
        pages.push(1, '...', page - 1, page, page + 1, '...', pageCount);
      }
    }

    return pages.map((p, idx) =>
      p === '...' ? (
        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
          {p}
        </span>
      ) : (
        <button
          key={`page-${p}`}
          onClick={() => onPageChange(Number(p))}
          aria-current={p === page ? 'page' : undefined}
          className={`px-2 py-1 text-sm rounded ${p === page ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          {p}
        </button>
      )
    );
  };

  return (
    <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`}>
      <div className="text-sm text-gray-600">
        Items per page:
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="ml-2 border border-gray-200 rounded px-2 py-1 text-sm"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex items-center gap-2 self-stretch sm:self-auto">
        <nav className="inline-flex items-center space-x-1" aria-label="Pagination">
          <button
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            aria-label="First page"
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronsLeft className="h-4 w-4" aria-hidden />
          </button>

          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Previous page"
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>

          <div className="hidden sm:inline-flex items-center space-x-1 px-2">{renderPages()}</div>

          <button
            onClick={() => onPageChange(Math.min(pageCount, page + 1))}
            disabled={page === pageCount}
            aria-label="Next page"
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>

          <button
            onClick={() => onPageChange(pageCount)}
            disabled={page === pageCount}
            aria-label="Last page"
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronsRight className="h-4 w-4" aria-hidden />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
